import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";

// Critical path — loaded eagerly
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./components/admin/AdminLayout";
import { AuthProvider } from "./context/AuthContext";
import { BookingsProvider } from "./context/BookingsContext";
import { PropertiesProvider } from "./context/PropertiesContext";
import ProtectedRoute from "./components/admin/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";
import { Loader2 } from "lucide-react";

// Lazy-loaded pages (code-split) — heavy charts/tables only load when route opens
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminProperties = lazy(() => import("./pages/admin/AdminProperties"));
const AdminBookings = lazy(() => import("./pages/admin/AdminBookings"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
const AdminNotifications = lazy(() => import("./pages/admin/AdminNotifications"));
const AdminAuditLogs = lazy(() => import("./pages/admin/AdminAuditLogs"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminLegalSettings = lazy(() => import("./pages/admin/AdminLegalSettings"));
const AdminWhatsAppInbox = lazy(() => import("./pages/admin/AdminWhatsAppInbox"));
const AdminWhatsAppQueue = lazy(() => import("./pages/admin/AdminWhatsAppQueue"));
const AdminGuestJourneys = lazy(() => import("./pages/admin/AdminGuestJourneys"));
const AdminLoginRecords = lazy(() => import("./pages/admin/AdminLoginRecords"));const AdminBfrEmail = lazy(() => import("./pages/admin/AdminBfrEmail"));const AdminGmailIntegration = lazy(() => import("./pages/admin/AdminGmailIntegration"));const AdminTutorials = lazy(() => import("./pages/admin/AdminTutorials"));const AdminWalkinBuilder = lazy(() => import("./pages/admin/AdminWalkinBuilder"));const LegalPublicPage = lazy(() => import("./pages/public/LegalPublicPage"));

const PageLoader = () => (
  <div className="flex items-center justify-center py-24">
    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
  </div>
);

function LastRouteRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    const last = localStorage.getItem('adminLastRoute');
    // Default landing = Bookings (lighter than Dashboard which has heavy charts)
    navigate(last && last.startsWith('/admin/') ? last : '/admin/bookings', { replace: true });
  }, [navigate]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/admin/login" element={<AdminLogin />} />

          {/* Public legal pages — no auth required, shareable */}
          <Route path="/privacy-policy" element={
            <Suspense fallback={<PageLoader />}>
              <LegalPublicPage policyType="privacy_policy" fallbackTitle="Privacy Policy" />
            </Suspense>
          } />
          <Route path="/terms-and-conditions" element={
            <Suspense fallback={<PageLoader />}>
              <LegalPublicPage policyType="terms_conditions" fallbackTitle="Terms & Conditions" />
            </Suspense>
          } />
          
          <Route path="/admin" element={
            <ProtectedRoute>
              <BookingsProvider>
                <PropertiesProvider>
                  <AdminLayout />
                </PropertiesProvider>
              </BookingsProvider>
            </ProtectedRoute>
          }>
            <Route index element={<LastRouteRedirect />} />
            <Route path="dashboard" element={<Suspense fallback={<PageLoader />}><AdminDashboard /></Suspense>} />
            <Route path="users" element={<Suspense fallback={<PageLoader />}><AdminUsers /></Suspense>} />
            <Route path="properties" element={<Suspense fallback={<PageLoader />}><AdminProperties /></Suspense>} />
            <Route path="bookings" element={<Suspense fallback={<PageLoader />}><AdminBookings /></Suspense>} />
            <Route path="whatsapp-inbox" element={<Suspense fallback={<PageLoader />}><AdminWhatsAppInbox /></Suspense>} />
            <Route path="whatsapp-queue" element={<Suspense fallback={<PageLoader />}><AdminWhatsAppQueue /></Suspense>} />
            <Route path="guest-journeys" element={<Suspense fallback={<PageLoader />}><AdminGuestJourneys /></Suspense>} />
            <Route path="login-records" element={<Suspense fallback={<PageLoader />}><AdminLoginRecords /></Suspense>} />
            <Route path="bfr-email" element={<Suspense fallback={<PageLoader />}><AdminBfrEmail /></Suspense>} />
            <Route path="gmail-integration" element={<Suspense fallback={<PageLoader />}><AdminGmailIntegration /></Suspense>} />
            <Route path="tutorials" element={<Suspense fallback={<PageLoader />}><AdminTutorials /></Suspense>} />
            <Route path="walkin-builder" element={<Suspense fallback={<PageLoader />}><AdminWalkinBuilder /></Suspense>} />
            <Route path="reviews" element={<Suspense fallback={<PageLoader />}><AdminReviews /></Suspense>} />
            <Route path="notifications" element={<Suspense fallback={<PageLoader />}><AdminNotifications /></Suspense>} />
            <Route path="audit-logs" element={<Suspense fallback={<PageLoader />}><AdminAuditLogs /></Suspense>} />
            <Route path="legal" element={<Suspense fallback={<PageLoader />}><AdminLegalSettings /></Suspense>} />
            <Route path="settings" element={<Suspense fallback={<PageLoader />}><AdminSettings /></Suspense>} />
          </Route>

          <Route path="/" element={<Navigate to="/admin/login" replace />} />
          <Route path="*" element={<Navigate to="/admin/login" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

export default App;
